import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useSettingsDivision } from 'components/settings/SettingsDivisionContext';
import { fetchData } from 'services/api/fetchData';
import { saveData } from 'services/api/saveData';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import Dropdown from 'components/forms/basefields/Dropdown';
import TimeInput from 'components/forms/basefields/TimeInput';
import TextareaInput, { TextareaInputHandle } from 'components/forms/basefields/TextareaInput';
import { Division } from '../../environments/EnvironmentTypes';
import { AUTOMATIC_SMS_MESSAGE_TYPES, smsTemplateLibraryDefaults } from './smsTemplateLibraryDefaults';
import {
    SmsTemplateTag,
    calculateSmsParts,
    estimateLengthRange,
    getCounterLevel,
    normalizeGsm7,
} from './smsCharacterCount';
import '../../../style/scss/forms.scss';
import '../../../style/scss/detailpage.scss';
import '../../../style/scss/sms.scss';

interface WorkflowOption {
    id: number;
    name: string;
}

// The example values used to render the phone preview
const PREVIEW_TAG_VALUES: Record<string, string> = {
    datum: 'morgen',
    starttijd: '8:00',
    eindtijd: '12:00',
    tijdvak: '8:00 en 12:00',
    medewerkers: 'Peter en Daan',
    monteur: 'Peter',
    reistijd: '20 minuten',
    nieuwe_tijd: '14:30',
    klantnaam: 'Jan',
    klant_link: 'crf.nl/a/x7k2',
};

const SmsTemplatePage: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { id, templateType } = useParams<{ id?: string; templateType?: string; }>();
    const { activeFeatures, handleLogout } = useAuthContext();
    const { setFloatingAlert } = useGlobalContext();
    const { environmentSettings } = useSettings();
    const { selectedDivision: settingsDivision, isEnvironmentSelected } = useSettingsDivision();
    const [formData, setFormData] = useState<Record<string, any> | null>(null);
    const [tags, setTags] = useState<SmsTemplateTag[]>([]);
    const [workflows, setWorkflows] = useState<WorkflowOption[]>([]);
    const [divisions, setDivisions] = useState<Division[]>([]);
    const [errors, setErrors] = useState<Record<string, any>>({});
    const [buttonLoader, setButtonLoader] = useState(false);
    const bodyInputRef = useRef<TextareaInputHandle>(null);
    const shouldFetchDivisions = activeFeatures.includes('divisions');

    // Fetch the template, the tag registry, the workflows and the divisions
    useEffect(() => {
        const fetchPageData = async () => {
            const [tagResponse, workflowResponse, divisionResponse, templateResponse] = await Promise.all([
                fetchData({ apiUrl: 'get_sms_template_tag_list', handleLogout }),
                fetchData({ apiUrl: 'get_workflow_list', params: { connected_feature: 'job', is_active: true, deleted: false }, handleLogout }),
                shouldFetchDivisions ? fetchData({ apiUrl: 'get_division_list', params: { is_active: true }, handleLogout }) : Promise.resolve(null),
                id ? fetchData({ apiObject: 'sms_template', itemId: Number(id), handleLogout }) : Promise.resolve(null),
            ]);
            setTags(tagResponse || []);
            setWorkflows(workflowResponse?.results || []);
            setDivisions(divisionResponse?.results || []);

            // Set the fetched template when editing an existing one
            if (templateResponse) {
                setFormData({
                    ...templateResponse,
                    send_time: templateResponse.send_time ? templateResponse.send_time.substring(0, 5) : null,
                });
                return;
            }

            // Otherwise initialize a new template from the library defaults
            const defaults = smsTemplateLibraryDefaults[templateType || ''] || { body: '', send_time: null };
            const divisionDefault = !isEnvironmentSelected && settingsDivision ? { division: settingsDivision.id } : {};
            setFormData({
                name: t(`sms_template.message_type.${templateType}`),
                message_type: templateType,
                body: defaults.body,
                send_time: defaults.send_time,
                client_entity_types: [],
                workflows: [],
                send_for: 'first_visit_day',
                allow_manual_send: true,
                is_active: true,
                ...divisionDefault,
            });
        };
        fetchPageData();
    }, [id, templateType]);

    // Determine whether the template is an automatic message type
    const messageType = formData?.message_type || templateType || '';
    const isAutomatic = AUTOMATIC_SMS_MESSAGE_TYPES.includes(messageType);

    // Get the tags that are available for the message type
    const availableTags = useMemo(() => (
        tags.filter(tag => tag.message_types.includes(messageType))
    ), [tags, messageType]);

    // Calculate the length estimate of the current body
    const lengthEstimate = useMemo(() => (
        estimateLengthRange(formData?.body || '', tags)
    ), [formData?.body, tags]);
    const counterLevel = getCounterLevel(lengthEstimate.max);
    const credits = calculateSmsParts(lengthEstimate.max);

    // Render the phone preview text with the example tag values
    const previewText = useMemo(() => {
        const rendered = (formData?.body || '').replace(/\{([a-z_]+)\}/g, (match: string, key: string) => (
            PREVIEW_TAG_VALUES[key] ?? match
        ));
        return normalizeGsm7(rendered);
    }, [formData?.body]);

    // Get the sender name of the division for the phone preview
    const previewSenderName = (!isEnvironmentSelected && settingsDivision
        ? settingsDivision.name.substring(0, 11)
        : environmentSettings?.sms_sender_name) || t('sms.sms_template.preview_sender_fallback');

    // Set the entity type dropdown options
    const entityTypeOptions = [
        { id: 1, value: 'all', name: t('sms.sms_template.entity_type_all') },
        { id: 2, value: 'person', name: t('sms.sms_template.entity_type_person') },
        { id: 3, value: 'organization', name: t('sms.sms_template.entity_type_organization') },
    ];

    // Determine the selected entity type option
    const selectedEntityTypes: string[] = formData?.client_entity_types || [];
    const selectedEntityTypeOption = entityTypeOptions.find(option => (
        selectedEntityTypes.length === 1 ? option.value === selectedEntityTypes[0] : option.value === 'all'
    ));

    // Update a single field of the form data
    const updateField = (name: string, value: any) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Insert a tag at the cursor position of the body
    const insertTag = (tagKey: string) => {
        if (!tagKey) return;
        bodyInputRef.current?.insertAtCursor(`{${tagKey}}`);
    };

    // Handle a change of the entity type dropdown
    const handleEntityTypeChange = (value: string) => {
        const option = entityTypeOptions.find(candidate => String(candidate.id) === value || candidate.value === value);
        updateField('client_entity_types', option && option.value !== 'all' ? [option.value] : []);
    };

    // Handle a change of the workflow dropdown
    const handleWorkflowChange = (value: string) => {
        if (value === 'none') {
            updateField('workflows', []);
            return;
        }
        updateField('workflows', [Number(value)]);
    };

    // Handle a change of the division dropdown
    const handleDivisionChange = (value: string) => {
        updateField('division', value === 'none' ? null : Number(value));
    };

    // Get the header title of the page
    const getHeaderTitle = (): string => {
        return t('sms.sms_template.page_title', { templateType: t(`sms_template.message_type.${messageType}`) });
    };

    // Navigate back to the sms settings
    const handleClose = () => {
        history.push('/settings/features/sms/settings');
    };

    // Submit the template to the backend
    const handleSubmit = async () => {
        if (!formData) return;
        setButtonLoader(true);
        setErrors({});

        try {
            // Save the template with the right endpoint
            const response = await saveData({
                apiObject: 'sms_template',
                itemId: id ? Number(id) : undefined,
                data: {
                    ...formData,
                    send_time: isAutomatic ? formData.send_time : null,
                    ...(id ? { id: Number(id) } : {}),
                }
            });

            // Go back to the sms settings on success
            if (response?.status === 200 || response?.status === 201) {
                setFloatingAlert({ type: 'success' });
                handleClose();
            }
        } catch (error: any) {
            // Show the validation errors of the backend
            if (error?.response?.data) setErrors(error.response.data);
        } finally {
            setButtonLoader(false);
        }
    };

    // Show a loader until the data is initialized
    if (!formData) {
        return (
            <div className='detailpage container-filled'>
                <div className='inline-form-loader'>
                    <div className='loader'></div>
                </div>
            </div>
        );
    }

    return (
        <div className='detailpage sms-template-page container-filled'>
            <div className='detailpage-header row'>
                <div className='header-title'>
                    <span className='back-button'
                          onClick={() => { id
                            ? history.push('/settings/features/sms/settings')
                            : history.push('/settings/features/sms/template-library')
                          }}>
                        <FontAwesomeIcon icon={faAngleLeft} />
                    </span>
                    <h2 className='fixed-title'>
                        {getHeaderTitle()}
                    </h2>
                </div>
            </div>
            <div className='detailpage-content'>
                <div>
                    {/* The base fields of the template */}
                    <div className='card'>
                        <h6 className='template-fields-header'>
                            {t('sms.sms_template.details_title')}
                        </h6>
                        <div className='field-group'>
                            <div className='edit-mode'>
                                <label>{t('sms.sms_template.name_label')}</label>
                                <input
                                    type='text'
                                    id='name'
                                    name='name'
                                    placeholder={t('sms.sms_template.name_placeholder')}
                                    value={formData.name || ''}
                                    onChange={(event) => updateField('name', event.target.value)}
                                    className={`text ${errors.name ? 'is-invalid' : ''}`}
                                />
                                {errors.name && (
                                    <div className='error-message'>{errors.name}</div>
                                )}
                            </div>
                            {isAutomatic && (
                                <div className='edit-mode'>
                                    <label>{t('sms.sms_template.send_time_label')}</label>
                                    <TimeInput
                                        name='send_time'
                                        type='time'
                                        value={formData.send_time || null}
                                        onChange={(time) => updateField('send_time', time)}
                                        isInvalid={!!errors.send_time}
                                    />
                                    <p className='helper-text'>{t('sms.sms_template.send_time_helper_text')}</p>
                                </div>
                            )}
                            <div className='edit-mode'>
                                <label>{t('sms.sms_template.entity_type_label')}</label>
                                <Dropdown<{ id: number, value: string, name: string }>
                                    options={entityTypeOptions}
                                    id='client_entity_types'
                                    name='client_entity_types'
                                    selectedOption={selectedEntityTypeOption}
                                    onChange={handleEntityTypeChange}
                                    selectionFormat={(option) => option.name}
                                    optionFormat='name'
                                    showSearch={false}
                                    allowNoneOption={false}
                                />
                                <p className='helper-text'>{t('sms.sms_template.entity_type_helper_text')}</p>
                                {selectedEntityTypes.includes('organization') && (
                                    <p className='helper-text'>{t('sms.sms_template.organization_recipient_explanation')}</p>
                                )}
                            </div>
                            {workflows.length > 0 && (
                                <div className='edit-mode'>
                                    <label>{t('sms.sms_template.workflows_label')}</label>
                                    <Dropdown<WorkflowOption>
                                        options={workflows}
                                        id='workflows'
                                        name='workflows'
                                        disabled_selected={t('forms.select_type_disabled_selected')}
                                        selectedOption={workflows.find(workflow => (formData.workflows || []).includes(workflow.id)) || null}
                                        onChange={handleWorkflowChange}
                                        selectionFormat={(option) => option.name}
                                        optionFormat='name'
                                        showSearch={false}
                                        allowNoneOption={true}
                                    />
                                    <p className='helper-text'>{t('sms.sms_template.workflows_helper_text')}</p>
                                </div>
                            )}
                            {activeFeatures.includes('divisions') && divisions.length > 0 && (
                                <div className='edit-mode'>
                                    <label>{t('sms.sms_template.division_label')}</label>
                                    <Dropdown<Division>
                                        options={divisions}
                                        id='division'
                                        name='division'
                                        disabled_selected={t('sms.sms_template.division_all_option')}
                                        selectedOption={divisions.find(division => formData.division === division.id) || null}
                                        onChange={handleDivisionChange}
                                        selectionFormat={(option) => option.name}
                                        optionFormat='name'
                                        showSearch={false}
                                        allowNoneOption={true}
                                        noneOptionLabel='sms.sms_template.division_all_option'
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* The message editor of the template */}
                    <div className='card'>
                        <h6 className='template-fields-header'>
                            {t('sms.sms_template.message_title')}
                        </h6>
                        <div className='field-group'>
                            <div className='edit-mode'>
                                <label>{t('sms.sms_template.body_label')}</label>
                                <TextareaInput
                                    ref={bodyInputRef}
                                    name='body'
                                    rows={3}
                                    value={formData.body || ''}
                                    placeholder={t('sms.sms_template.body_placeholder')}
                                    viewInEditMode={true}
                                    isInvalid={!!errors.body}
                                    onChange={(value) => {
                                        const newValue = typeof value === 'string' ? value : value.target.value;
                                        updateField('body', newValue);
                                    }}
                                />
                                <p className={`helper-text sms-character-counter counter-${counterLevel}`}>
                                    {lengthEstimate.min === lengthEstimate.max
                                        ? t('sms.sms_template.character_count_exact', { count: lengthEstimate.max, credits })
                                        : t('sms.sms_template.character_count_range', { min: lengthEstimate.min, max: lengthEstimate.max, credits })}
                                </p>
                                {counterLevel === 'error' && (
                                    <p className='error-text'>{t('sms.sms_template.character_count_error')}</p>
                                )}
                                {errors.body && (
                                    <div className='error-message'>{errors.body}</div>
                                )}
                            </div>
                            <div className='edit-mode'>
                                <label>{t('sms.sms_template.insert_tag_label')}</label>
                                <Dropdown<SmsTemplateTag>
                                    options={availableTags}
                                    id='insert_tag'
                                    name='insert_tag'
                                    disabled_selected={t('sms.sms_template.insert_tag_disabled_selected')}
                                    selectedOption={null}
                                    onChange={(value) => {
                                        const tag = availableTags.find(candidate => candidate.key === value);
                                        insertTag(tag?.key || '');
                                    }}
                                    backendField='key'
                                    selectionFormat={(option) => `{${option.key}}`}
                                    optionFormat={(option) => `{${option.key}} - ${t(option.label_key)}`}
                                    showSearch={false}
                                    allowNoneOption={false}
                                />
                                <p className='helper-text'>{t('sms.sms_template.insert_tag_helper_text')}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    {/* The phone preview of the message */}
                    <div className='card sms-preview-card'>
                        <h6 className='template-fields-header'>
                            {t('sms.sms_template.preview_title')}
                        </h6>
                        <div className='sms-phone-preview'>
                            <div className='sms-phone-screen'>
                                <div className='sms-phone-sender'>
                                    <div className='sms-phone-sender-avatar'>
                                        {previewSenderName.substring(0, 1).toUpperCase()}
                                    </div>
                                    <span className='sms-phone-sender-name'>{previewSenderName}</span>
                                </div>
                                <div className='sms-phone-conversation'>
                                    <div className='sms-phone-bubble'>
                                        {previewText || t('sms.sms_template.preview_empty_message')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='footer-fixed'>
                <div className='container-filled'>
                    <SecondaryButton
                        onClick={handleClose}
                        label='general.cancel'
                        size='small'
                    />
                    <PrimaryButton
                        onClick={handleSubmit}
                        label='general.save'
                        size='small'
                        loading={buttonLoader}
                    />
                </div>
            </div>
        </div>
    );
};

export default SmsTemplatePage;
